import React from 'react';
import { CircularProgress, CircularProgressProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type SpinnerSize = 'small' | 'medium' | 'large';

export interface SpinnerProps
  extends Omit<CircularProgressProps, 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** Delay after which spinner should be visible. */
  delay?: number;

  /** The size of the spinner */
  size?: SpinnerSize;

  /** Additional styles to apply (combined with pounce props) */
  sx?: CircularProgressProps['sx'];
}

/**
 * PounceV2 Spinner component built on top of MUI CircularProgress.
 *
 * Provides the same API and functionality as the original Pounce Spinner
 * including delay functionality, size variants, and color theming.
 *
 * Features:
 * - Delay prop for conditional visibility
 * - Size variants (small, medium, large)
 * - Color theming support
 * - Full pounce style props compatibility
 * - Accessibility features from MUI
 */
export const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { delay = 0, size = 'medium', sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const [isVisible, setVisibility] = React.useState(delay === 0);
  const delayTimer = React.useRef<number>(0);

  React.useEffect(() => {
    if (delay && delay > 0) {
      delayTimer.current = window.setTimeout(() => setVisibility(true), delay);
    }
    return () => clearTimeout(delayTimer.current);
  }, [delay]);

  if (!isVisible) {
    return null;
  }

  // Combine pounce-generated styles with user-provided sx
  // This will include any color prop passed through pounce props (like color="blue-400")
  const combinedSx = {
    ...pounceStyles,
    ...sx,
  };

  return (
    <CircularProgress
      ref={ref}
      size={size}
      sx={combinedSx}
      aria-label="Loading..."
      {...otherProps}
    />
  );
});
